import { DockviewReact, type DockviewApi, type DockviewReadyEvent, type DockviewTheme, type IDockviewPanelProps, type SerializedDockview } from 'dockview-react';
import { Command, Moon, PanelLeft, PanelRight, Search, Sun, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DiagramView } from '../diagram/DiagramView';
import { cn } from '../lib/cn';
import { db } from '../lib/db';
import { EXPLORER_DOCKS, INSPECTOR_DOCKS, TOUCH, useMedia } from '../lib/media';
import { useTheme } from '../lib/theme';
import { closeAllPanels, NO_INSPECTOR, openCodeMap, openDiagram, openFiles, setActiveTab, setDockApi, tabKind, useActiveTab } from '../state/dock';
import { closeDrawer, toggleSidebar, useSidebars, type Side } from '../state/sidebars';
import { closeSource, findSet, setActiveRef, useWorkspace, type ModelRef } from '../state/workspace';
import { CopyForAgentDialog } from '../agent/CopyForAgent';
import { CodeMapView } from '../code/CodeMapView';
import { EndpointMapView } from '../code/EndpointMapView';
import { TestMapView } from '../code/TestMapView';
import { CompareView } from '../compare/CompareView';
import { FilesView } from '../files/FilesView';
import { GraphView } from '../notes/GraphView';
import { NoteView } from '../notes/NoteView';
import { PasteDialog } from '../sketch/PasteDialog';
import { SketchView } from '../sketch/SketchView';
import { mappedFiles, useCodeMap, type CodeView } from '../state/codemap';
import { dismissToast, useToasts } from '../state/toast';
import { TimelineView } from '../timeline/TimelineView';
import { CommandPalette, openPalette } from './CommandPalette';
import { Explorer } from './Explorer';
import { Inspector } from './Inspector';
import { SourceView } from './SourceView';
import { StatusBar } from './StatusBar';

function DiagramPanel(props: IDockviewPanelProps<{ modelRef: ModelRef }>) {
  return <DiagramView modelRef={props.params.modelRef} />;
}

function SourcePanel(props: IDockviewPanelProps<{ path: string; line?: number; nonce?: number }>) {
  return <SourceView path={props.params.path} line={props.params.line} nonce={props.params.nonce} />;
}

function TimelinePanel(props: IDockviewPanelProps<{ setKey: string; migrationId: string | null; nonce?: number }>) {
  return <TimelineView setKey={props.params.setKey} migrationId={props.params.migrationId} nonce={props.params.nonce} api={props.api} />;
}

function SketchPanel(props: IDockviewPanelProps<{ sketchId: string }>) {
  return <SketchView sketchId={props.params.sketchId} api={props.api} />;
}

function ComparePanel(props: IDockviewPanelProps<{ refs: ModelRef[] }>) {
  return <CompareView refs={props.params.refs} api={props.api} />;
}

function NotePanel(props: IDockviewPanelProps<{ noteId: string }>) {
  return <NoteView noteId={props.params.noteId} api={props.api} />;
}

function GraphPanel() {
  return <GraphView />;
}

function CodeMapPanel() {
  return <CodeMapView />;
}

function EndpointMapPanel() {
  return <EndpointMapView />;
}

function TestMapPanel() {
  return <TestMapView />;
}

function FilesPanel() {
  return <FilesView />;
}

const components = { diagram: DiagramPanel, source: SourcePanel, timeline: TimelinePanel, sketch: SketchPanel, compare: ComparePanel, note: NotePanel, graph: GraphPanel, codemap: CodeMapPanel, endpoints: EndpointMapPanel, tests: TestMapPanel, files: FilesPanel };

/** The code view a panel shows, by its id. */
const CODE_VIEWS: Record<string, CodeView> = { codemap: 'code', endpoints: 'endpoints', tests: 'tests' };

type PanelParams = { modelRef?: ModelRef; path?: string; setKey?: string; migrationId?: string | null; sketchId?: string; refs?: ModelRef[]; noteId?: string };

function refStillValid(ref: ModelRef): boolean {
  if (ref.sketchId) return !!useWorkspace.getState().sketches[ref.sketchId];
  const set = findSet(ref.setKey);
  return !!set && (ref.migrationId === null || set.migrations.some((m) => m.id === ref.migrationId));
}

function Toasts() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-9 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2" role="status" aria-live="polite">
      {toasts.map((t) => (
        <button
          key={t.id}
          onClick={() => dismissToast(t.id)}
          className={cn(
            'pointer-events-auto max-w-[min(560px,90vw)] rounded-lg border px-3 py-2 text-left text-[12.5px] shadow-[var(--shadow-pop)]',
            t.kind === 'error' ? 'border-removed/50 bg-panel-2 text-text' : 'border-border-strong bg-panel-2 text-text',
          )}
        >
          {t.kind === 'error' && <span className="mr-1.5 font-semibold text-removed">Can't do that:</span>}
          {t.text}
        </button>
      ))}
    </div>
  );
}

/** A saved layout is only reused if every tab still points at something that exists. */
function layoutStillValid(saved: SerializedDockview): boolean {
  const files = new Set(useWorkspace.getState().files.map((f) => f.path));
  return Object.values(saved.panels ?? {}).every((panel) => {
    const params = (panel as { params?: PanelParams }).params;
    if (params?.modelRef) return refStillValid(params.modelRef);
    if (params?.sketchId) return !!useWorkspace.getState().sketches[params.sketchId];
    if (params?.noteId) return !!useWorkspace.getState().notes[params.noteId];
    if (params?.refs) return params.refs.every(refStillValid);
    if (params?.setKey) return !!findSet(params.setKey);
    if (params?.path) return files.has(params.path);
    return true;
  });
}

function Watermark() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-muted">
      <p>Open a model from the explorer.</p>
      <p className="text-[12px] text-faint pointer-coarse:hidden">
        <kbd>Ctrl</kbd> <kbd>K</kbd> jumps to any entity or migration
      </p>
    </div>
  );
}

function usePersistentWidth(key: string, initial: number): [number, (w: number) => void] {
  const [width, setWidth] = useState(() => {
    const saved = Number(localStorage.getItem(key));
    return saved >= 160 && saved <= 800 ? saved : initial;
  });
  const set = useCallback(
    (w: number) => {
      setWidth(w);
      localStorage.setItem(key, String(Math.round(w)));
    },
    [key],
  );
  return [width, set];
}

function Resizer({ onResize, side }: { onResize: (dx: number) => void; side: 'left' | 'right' }) {
  const start = useRef<number | null>(null);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      className="group relative w-px flex-none cursor-col-resize bg-border"
      onPointerDown={(e) => {
        start.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (start.current === null) return;
        const dx = e.clientX - start.current;
        start.current = e.clientX;
        onResize(side === 'left' ? dx : -dx);
      }}
      onPointerUp={() => {
        start.current = null;
      }}
    >
      <span className="absolute inset-y-0 -left-1 -right-1 group-hover:bg-accent/40" />
    </div>
  );
}

function IconButton({ label, onClick, children, active, disabled, className }: { label: string; onClick: () => void; children: React.ReactNode; active?: boolean; disabled?: boolean; className?: string }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      className={cn(
        'flex h-7 w-7 flex-none items-center justify-center rounded-md hover:bg-hover hover:text-text disabled:pointer-events-none disabled:opacity-35 pointer-coarse:h-9 pointer-coarse:w-9',
        active ? 'text-text' : 'text-muted',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * A sidebar slid in over the tabs, on screens too narrow to keep it beside them. Opening
 * something from it (a model, a migration, an entity) slides it away again.
 */
function Drawer({ side, open, children }: { side: Side; open: boolean; children: React.ReactNode }) {
  return (
    <>
      <div className={cn('mv-scrim absolute inset-0 z-30', open && 'open')} onClick={closeDrawer} aria-hidden />
      <div
        className={cn('mv-drawer absolute inset-y-0 z-40 flex flex-col bg-panel', side === 'explorer' ? 'left-0 border-r' : 'right-0 border-l', 'border-border', open && 'open')}
        data-side={side}
        data-testid={`${side}-drawer`}
        inert={!open}
        onClickCapture={(e) => {
          // Items that open something (no expand arrow) close the drawer; expanding a group does not.
          const item = (e.target as Element).closest('[role="treeitem"]');
          if (item && !item.hasAttribute('aria-expanded')) closeDrawer();
        }}
      >
        {children}
      </div>
    </>
  );
}

export function Workspace() {
  const source = useWorkspace((s) => s.source);
  const discovery = useWorkspace((s) => s.discovery);
  const { theme, toggle } = useTheme();
  const [left, setLeft] = usePersistentWidth('mv-left-width', 272);
  const [right, setRight] = usePersistentWidth('mv-right-width', 320);
  const sidebars = useSidebars();
  const explorerDocks = useMedia(EXPLORER_DOCKS);
  const inspectorDocks = useMedia(INSPECTOR_DOCKS);
  const touch = useMedia(TOUCH);
  const explorerOpen = explorerDocks ? sidebars.explorer : sidebars.drawer === 'explorer';
  // The Files tab and the graph have nothing for the inspector to show: it steps aside for them.
  const inspectorUseful = useActiveTab((s) => !NO_INSPECTOR.has(tabKind(s.subject) ?? ''));
  const inspectorOpen = inspectorUseful && (inspectorDocks ? sidebars.inspector : sidebars.drawer === 'inspector');
  const [dock, setDock] = useState<DockviewApi | null>(null);

  const dockTheme: DockviewTheme = { name: 'modelviewer', className: 'dockview-theme-modelviewer', colorScheme: theme };

  // A drawer goes away when what it opened is shown: another tab, or an entity brought into view.
  useEffect(() => useWorkspace.subscribe((s, prev) => s.reveal !== prev.reveal && closeDrawer()), []);
  useEffect(() => {
    if (!sidebars.drawer) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeDrawer();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebars.drawer]);
  // A drawer left open when the window grows wide enough to dock its sidebar.
  useEffect(() => {
    if ((sidebars.drawer === 'explorer' && explorerDocks) || (sidebars.drawer === 'inspector' && inspectorDocks)) closeDrawer();
  }, [sidebars.drawer, explorerDocks, inspectorDocks]);

  const onReady = (event: DockviewReadyEvent) => {
    setDockApi(event.api);
    setDock(event.api);
    event.api.onDidActivePanelChange(({ panel }) => {
      closeDrawer();
      useCodeMap.setState({ view: (panel && CODE_VIEWS[panel.id]) ?? null });
      setActiveTab(panel?.id ?? null);
      const params = panel?.params as { modelRef?: ModelRef } | undefined;
      if (params?.modelRef) setActiveRef(params.modelRef);
    });
    event.api.onDidRemovePanel(() => {
      const front = event.api.activePanel?.id;
      if (!front || !CODE_VIEWS[front]) useCodeMap.setState({ view: null });
      setActiveTab(front ?? null);
      const params = event.api.panels.map((p) => p.params as PanelParams);
      if (params.every((p) => !p.modelRef && !p.setKey)) setActiveRef(null);
      else if (params.every((p) => !p.setKey)) setActiveRef(useWorkspace.getState().activeRef);
    });
  };

  useEffect(
    () => () => {
      setDockApi(null);
      setActiveTab(null);
    },
    [],
  );

  // When a folder opens: restore its saved tabs, or show the largest context's current model.
  const restored = useRef(false);
  useEffect(() => {
    if (!dock || !discovery || !source || restored.current) return;
    restored.current = true;
    const layoutKey = `dock:${source.id}`;
    void (async () => {
      const saved = (await db.settings.get(layoutKey))?.value as SerializedDockview | undefined;
      if (saved && dock.panels.length === 0 && layoutStillValid(saved)) {
        try {
          dock.fromJSON(saved);
        } catch {
          dock.clear();
        }
      }
      if (dock.panels.length === 0) {
        const first = [...discovery.sets].sort((a, b) => b.migrations.length - a.migrations.length)[0];
        if (first) openDiagram({ setKey: first.key, migrationId: null });
        // No EF Core migrations, but C# code: show the code map; neither: the files.
        else if (mappedFiles().length) openCodeMap();
        else openFiles();
      }
      let timer: number | undefined;
      dock.onDidLayoutChange(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => void db.settings.put({ key: layoutKey, value: dock.toJSON() }), 400);
      });
    })();
  }, [dock, discovery, source]);

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-10 flex-none items-center gap-2 border-b border-border bg-panel px-2 pointer-coarse:h-12 pointer-coarse:gap-1">
        <IconButton label={explorerOpen ? 'Hide explorer' : 'Show explorer'} onClick={() => toggleSidebar('explorer')} active={explorerOpen}>
          <PanelLeft size={16} />
        </IconButton>
        <img src="/favicon.svg" alt="" className="h-5 w-5 flex-none" />
        <span className="font-semibold max-md:hidden">modelviewer</span>
        <span className="text-faint max-md:hidden">/</span>
        <span className="min-w-0 truncate text-muted max-md:font-medium max-md:text-text">{source?.label}</span>
        <button
          onClick={() => openPalette.current()}
          className="mx-auto flex h-7 w-[min(420px,40vw)] flex-none items-center gap-2 rounded-md border border-border bg-bg px-2.5 text-[12.5px] whitespace-nowrap text-faint hover:border-border-strong max-md:hidden"
        >
          <Command size={13} /> <span className="truncate">Jump to entity, migration or action</span>
          <span className="ml-auto pointer-coarse:hidden">
            <kbd>Ctrl</kbd> <kbd>K</kbd>
          </span>
        </button>
        <IconButton label="Jump to entity, migration or action" onClick={() => openPalette.current()} className="ml-auto md:hidden">
          <Search size={16} />
        </IconButton>
        <IconButton label={theme === 'dark' ? 'Use light theme' : 'Use dark theme'} onClick={toggle}>
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </IconButton>
        <IconButton
          label="Close this folder"
          onClick={() => {
            closeAllPanels();
            closeSource();
          }}
        >
          <X size={16} />
        </IconButton>
        <IconButton
          label={!inspectorUseful ? 'This tab has nothing to inspect' : inspectorOpen ? 'Hide inspector' : 'Show inspector'}
          onClick={() => toggleSidebar('inspector')}
          active={inspectorOpen}
          disabled={!inspectorUseful}
        >
          <PanelRight size={16} />
        </IconButton>
      </header>

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {explorerDocks && sidebars.explorer && (
          <>
            <div className="flex-none bg-panel" style={{ width: left }}>
              <Explorer />
            </div>
            <Resizer side="left" onResize={(dx) => setLeft(Math.min(560, Math.max(180, left + dx)))} />
          </>
        )}
        <div className="min-w-0 flex-1">
          <DockviewReact
            theme={dockTheme}
            components={components}
            onReady={onReady}
            watermarkComponent={Watermark}
            // Narrow: one group of tabs, no splitting by dragging. Touch: the tab strip scrolls with a swipe.
            disableDnd={!explorerDocks}
            scrollbars={touch ? 'native' : 'custom'}
          />
        </div>
        {inspectorDocks && inspectorOpen && (
          <>
            <Resizer side="right" onResize={(dx) => setRight(Math.min(640, Math.max(220, right + dx)))} />
            <div className="flex-none bg-panel" style={{ width: right }}>
              <Inspector />
            </div>
          </>
        )}
        {!explorerDocks && (
          <Drawer side="explorer" open={sidebars.drawer === 'explorer'}>
            <Explorer />
          </Drawer>
        )}
        {!inspectorDocks && (
          <Drawer side="inspector" open={inspectorOpen}>
            <Inspector />
          </Drawer>
        )}
      </div>

      <StatusBar />
      <CommandPalette />
      <PasteDialog />
      <CopyForAgentDialog />
      <Toasts />
    </div>
  );
}
